import axios from 'axios';

const backendUrl = process.env.DEV_MODE === 'true'
  ? process.env.SPEEM_BACKEND_DEV : process.env.SPEEM_BACKEND;

function authError(message: string): Error {
  const error: any = new Error(message);
  error.code = 'AUTH_REQUIRED';
  return error;
}

function payload(token: string): any {
  const encoded = token.split('.')[1];
  if (!encoded) throw authError('Please sign in again.');
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64 + '='.repeat((4 - base64.length % 4) % 4)));
}

export async function fetchBackendChallenge(username: string): Promise<string> {
  const response = await axios.post(`${backendUrl}/api/auth/challenge`, { username });
  if (!response.data?.success || typeof response.data.challenge !== 'string') {
    throw authError('Unable to request a login challenge. Please retry.');
  }
  return response.data.challenge;
}

export async function refreshBackendToken(token: string): Promise<any> {
  const response = await axios.post(`${backendUrl}/api/auth/refresh`, { token });
  return response.data;
}

export async function getBackendHeaders(expectedUser?: string): Promise<Record<string, string>> {
  const username = (localStorage.getItem('user') || '').toLowerCase();
  let token = localStorage.getItem('jwt_token') || '';
  if (!username || !token || token === 'undefined'
      || (expectedUser && expectedUser.toLowerCase() !== username)) {
    throw authError('Please sign in to the account that owns this upload.');
  }
  let claims: any;
  try { claims = payload(token); }
  catch { throw authError('Please sign in again.'); }
  if (claims.username?.toLowerCase() !== username) {
    throw authError('Please sign in again to the current account.');
  }
  // Client decoding selects refresh timing only; the backend verifies identity.
  if (!Number.isFinite(claims.exp) || claims.exp * 1000 < Date.now() + 60000) {
    let refreshed: any;
    try { refreshed = await refreshBackendToken(token); }
    catch { throw authError('Your session expired. Please sign in again.'); }
    if (!refreshed?.success || typeof refreshed.token !== 'string'
        || refreshed.username?.toLowerCase() !== username) {
      throw authError('Your session expired. Please sign in again.');
    }
    if ((localStorage.getItem('user') || '').toLowerCase() !== username) {
      throw authError('The active account changed. Resume from the correct account.');
    }
    token = refreshed.token;
    localStorage.setItem('jwt_token', token);
    try {
      const saved = JSON.parse(localStorage.getItem('saved_accounts') || '[]');
      if (Array.isArray(saved)) {
        localStorage.setItem('saved_accounts', JSON.stringify(saved.map(account =>
          account.username?.toLowerCase() === username ? { ...account, token } : account)));
      }
    } catch {
      // Invalid optional saved-account data must not block the current verified session.
    }
  }
  if ((localStorage.getItem('user') || '').toLowerCase() !== username) {
    throw authError('The active account changed. Resume from the correct account.');
  }
  return { Authorization: `Bearer ${token}` };
}


// Route private operations through the application's verified session.
function assertBackendUrl(input: string): void {
  const target = new URL(input, window.location.origin);
  const allowed = new URL(backendUrl, window.location.origin);
  if (target.origin !== allowed.origin || !target.pathname.startsWith(allowed.pathname.replace(/\/$/, '') + '/api/')) {
    throw new Error('Authenticated requests must use the Speem backend.');
  }
}

export const authenticatedApi = {
  async post(url: string, data?: any, config: any = {}) {
    assertBackendUrl(url);
    return axios.post(url, data, { ...config, headers: { ...config.headers, ...await getBackendHeaders() } });
  },
  async get(url: string, config: any = {}) {
    assertBackendUrl(url);
    return axios.get(url, { ...config, headers: { ...config.headers, ...await getBackendHeaders() } });
  }
};

export async function backendFetch(url: string, init: RequestInit = {}): Promise<Response> {
  assertBackendUrl(url);
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(await getBackendHeaders())) headers.set(key, value);
  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    const body = await response.clone().json().catch(() => ({}));
    throw new Error(body.message || body.error || body.detail || 'The request could not be completed.');
  }
  return response;
}
